import type { SxProps, Theme } from '@mui/material/styles'
import { uiPalette } from '@/theme/uiPalette'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'

/**
 * Token bảng danh sách — màu accent đọc từ CSS variables (`ThemeAccentProvider`),
 * fallback trùng `uiPalette` khi chưa hydrate.
 */
export const TABLE_HEAD_BG = `var(--banhang-table-head-bg, ${uiPalette.table.headBg})`
export const TABLE_HEAD_BORDER = `var(--banhang-table-head-border, ${uiPalette.table.headBorder})`
/** Viền ngoài khối bảng + phân trang (theo chủ đề). */
export const TABLE_WRAPPER_BORDER = `var(--banhang-table-wrapper-border, ${uiPalette.table.wrapperBorder})`
/** Kẻ ngang giữa các dòng — xám cố định Kiot (#eeeeee), không theo chủ đề. */
export const TABLE_ROW_DIVIDER = '#eeeeee'
/** Tiêu đề cột — gần Kiot (đậm, xám đen hơn slate app). */
export const TABLE_HEAD_TEXT = banhangKiotLike.kiotTextPrimary
/** Dòng chẵn (zebra). */
export const TABLE_BODY_BG = '#ffffff'
/** Dòng lẻ — tint màu chủ đề rất nhạt (xám trung tính trông bẩn cạnh header / hover có màu). */
export const TABLE_ROW_ALT_BG = 'var(--banhang-row-alt-bg, #f3f7fc)'
/** Chữ ô dữ liệu — trùng Kiot oldman (#333). */
export const TABLE_BODY_TEXT = banhangKiotLike.kiotTextPrimary
/** Font weight ô dữ liệu — Kiot oldman để nét thường (400), không medium. */
export const TABLE_BODY_CELL_FONT_WEIGHT = 400
/** Nền vùng chứa khối bảng bo góc — trùng nền sheet trang (tránh wedge trắng ở 4 góc). */
export const TABLE_LIST_CHROME_BG = banhangKiotLike.listSheetBg
export const TABLE_ROW_HOVER_BG = `var(--banhang-row-hover-bg, ${uiPalette.table.rowHoverBg})`
/** Dòng chọn (click) — highlight theo chủ đề. */
export const TABLE_ROW_SELECTED_BG = `var(--banhang-row-selected-bg, ${uiPalette.table.rowHoverBg})`
export const TABLE_ROW_SELECTED_HOVER_BG = 'var(--banhang-row-selected-hover-bg, #c5e0f5)'
/** Dòng đang expand (click mở panel) — xanh lá nhạt Kiot (`#00b63e` × 10% + trắng). */
export const TABLE_ROW_EXPANDED_BG = '#e6f8ec'

/** Sx `TableRow` khi isSelected — có hover đậm hơn. */
export const tableListRowSelectedRowSx = {
  '& .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
  '&:hover .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_HOVER_BG} !important`,
  },
} as const

/** Dòng đang chọn + panel expand — không đổi màu khi hover. */
export const tableListRowSelectedRowQuietHoverSx = {
  '& .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
  '&:hover .MuiTableCell-root': {
    backgroundColor: `${TABLE_ROW_SELECTED_BG} !important`,
  },
} as const

/** Dòng tổng (summary) — cam nhạt kiểu Kiot (`#fff3e6`). */
export const TABLE_SUMMARY_BG = banhangKiotLike.invoiceSummaryRowBg
export const TABLE_PAGINATION_BG = '#ffffff'
/** Trang hiện tại: nút xanh lá Kiot Hàng hóa. */
export const TABLE_PAGINATION_ACTIVE_BG = banhangKiotLike.posButtonGreen
export const TABLE_PAGINATION_ACTIVE_HOVER = banhangKiotLike.posButtonGreenHover
/** Bo góc nhẹ khối bảng + phân trang (một surface). */
export const TABLE_LIST_SURFACE_RADIUS_PX = 8
/** Bo góc nút trang trong `Pagination` (nhẹ). */
export const TABLE_PAGINATION_ITEM_RADIUS_PX = 4
export const TABLE_CONTROL_BORDER = '#d0d0d0'
export const TABLE_CONTROL_BORDER_HOVER = '#b0b0b0'

/** @deprecated Giữ tên cũ — trùng `TABLE_ROW_DIVIDER`. */
export const TABLE_CELL_BORDER = TABLE_ROW_DIVIDER

/** Chiều cao track scroll ngang (macOS không custom được scrollbar trên vùng cao). */
export const TABLE_LIST_SCROLLBAR_SIZE_PX = 6

export const tableListScrollShellSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  minWidth: 0,
  '--banhang-table-scrollbar-size': `${TABLE_LIST_SCROLLBAR_SIZE_PX}px`,
}

/** Vùng cuộn chính (dọc + ngang). */
export const tableListScrollMainSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  overscrollBehavior: 'contain',
  /** Chặn swipe back/forward macOS khi overscroll ngang bảng. */
  overscrollBehaviorX: 'none',
  overflowAnchor: 'none',
  scrollbarGutter: 'stable',
  touchAction: 'pan-x pan-y',
  bgcolor: TABLE_BODY_BG,
  /**
   * `scrollbarGutter: stable` để khe trắng bên phải header sticky (cột Thao tác).
   * Sơn dải nền header phủ luôn gutter ở mép trên viewport cuộn.
   */
  backgroundImage: `linear-gradient(${TABLE_HEAD_BG}, ${TABLE_HEAD_BG})`,
  backgroundSize: '100% 42px',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '0 0',
}

/** @deprecated Dùng `tableListScrollMainSx` + `TableListScrollArea`. */
export const tableListScrollRootSx = tableListScrollMainSx

export const tableListScrollAreaSx: SxProps<Theme> = {}
